"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"

const campaignPlatforms = [
  {
    name: "Meta Ads",
    spend: 12500,
    totalBudget: 15000,
    leads: 456,
    color: "bg-chart-4",
  },
  {
    name: "Google Ads",
    spend: 8900,
    totalBudget: 12000,
    leads: 312,
    color: "bg-chart-2",
  },
  {
    name: "TikTok Ads",
    spend: 3200,
    totalBudget: 5000,
    leads: 189,
    color: "bg-chart-5",
  },
]

const spChannels = [
  {
    name: "Roadside Jobs",
    spend: 12500,
    totalBudget: 15000,
    leads: 456,
    color: "bg-chart-4",
  },
  {
    name: "Fleet Contracts",
    spend: 8900,
    totalBudget: 12000,
    leads: 312,
    color: "bg-chart-2",
  },
  {
    name: "After-Hours Dispatch",
    spend: 3200,
    totalBudget: 5000,
    leads: 189,
    color: "bg-chart-5",
  },
]

interface PlatformData {
  name: string
  spend: number
  totalBudget: number
  leads: number
  color: string
}

interface PlatformBreakdownProps {
  title?: string
  variant?: "campaign" | "sp"
  items?: PlatformData[]
  data?: PlatformData[]
  loading?: boolean
}

const colorMap: Record<string, string> = {
  "meta": "bg-chart-4",
  "facebook": "bg-chart-4",
  "instagram": "bg-chart-4",
  "google": "bg-chart-2",
  "google_ads": "bg-chart-2",
  "tiktok": "bg-chart-5",
  "linkedin": "bg-chart-3",
  "twitter": "bg-chart-1",
  "x": "bg-chart-1",
}

export function PlatformBreakdown({
  title,
  variant = "campaign",
  items,
  data,
  loading = false,
}: PlatformBreakdownProps) {
  const defaultTitle = variant === "sp" ? "Dispatch Status Breakdown" : "Platform Breakdown"
  // Prefer explicit data/items props; fall back to built-in defaults
  const platforms =
    data && data.length > 0
      ? data
      : items && items.length > 0
      ? items
      : variant === "sp"
      ? spChannels
      : campaignPlatforms

  const totalSpend = platforms.reduce((acc, p) => acc + p.spend, 0)

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="text-card-foreground">{title || defaultTitle}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="text-center text-muted-foreground py-8">Loading...</div>
        ) : platforms.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">No platform data</div>
        ) : (
          <>
            {platforms.map((platform) => (
              <div key={platform.name} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`h-3 w-3 rounded-full ${platform.color}`} />
                    <span className="text-sm font-medium text-card-foreground">
                      {platform.name}
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {variant === "sp"
                      ? `${platform.spend.toLocaleString()} / ${platform.totalBudget.toLocaleString()} jobs`
                      : `$${platform.spend.toLocaleString()} / $${platform.totalBudget.toLocaleString()}`}
                  </span>
                </div>
                <Progress
                  value={platform.totalBudget > 0 ? (platform.spend / platform.totalBudget) * 100 : 0}
                  className="h-2 bg-secondary"
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{variant === "sp" ? `${platform.leads} jobs` : `${platform.leads} leads`}</span>
                  <span>
                    {variant === "sp"
                      ? `Share: ${platform.totalBudget > 0 ? ((platform.spend / platform.totalBudget) * 100).toFixed(1) : "0.0"}%`
                      : `CPL: $${platform.leads > 0 ? (platform.spend / platform.leads).toFixed(2) : "0.00"}`}
                  </span>
                </div>
              </div>
            ))}

            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-card-foreground">
                  {variant === "sp" ? "Total Jobs" : "Total Spend"}
                </span>
                <span className="text-lg font-bold text-card-foreground">
                  {variant === "sp" ? totalSpend.toLocaleString() : `$${totalSpend.toLocaleString()}`}
                </span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

export { colorMap as platformColorMap }
